import React, { useState } from 'react';
import {
  FiShield, FiPhone, FiMapPin, FiUser, FiAlertCircle,
  FiCheckCircle, FiLock, FiEye, FiHeart, FiStar,
  FiChevronRight, FiArrowRight, FiCheck,
} from 'react-icons/fi';
import {
  MdOutlineVerifiedUser, MdOutlineLocalPolice,
  MdOutlineHealthAndSafety, MdOutlineSupportAgent,
  MdOutlineEmergency, MdOutlineGppGood, MdOutlineShareLocation,
} from 'react-icons/md';
import {
  BsShieldCheck, BsPersonCheck, BsCameraVideo,
  BsStarFill, BsPhoneVibrate,
} from 'react-icons/bs';
import { RiUserLocationLine, RiAlertLine } from 'react-icons/ri';
import { HiOutlineBadgeCheck, HiOutlineLocationMarker } from 'react-icons/hi';
import { TbSos } from 'react-icons/tb';
import image from "../Assests/Untitled design (15).png"
import { motion } from 'framer-motion';

const RED       = '#e71204';
const RED2      = '#ff3a1f';
const LIGHT_RED = '#fff5f4';
const BORDER_RED= 'rgba(231,18,4,0.15)';

/* ─── DATA ──────────────────────────────────────────────────────────────────── */
const pillars = [
  {
    Icon: BsPersonCheck,
    color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe',
    title: 'Verified Drivers',
    desc: 'Every driver undergoes police background checks, license validation, and in-person onboarding before their first ride.',
    points: ['Police verification', 'License & RC check', 'In-person onboarding', 'Periodic re-audits'],
  },
  {
    Icon: MdOutlineShareLocation,
    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0',
    title: 'Live Trip Tracking',
    desc: 'Share your real-time location with family or friends during every ride. Know exactly where you are, always.',
    points: ['Real-time GPS tracking', 'Share trip with contacts', 'Route deviation alerts', 'ETA updates'],
  },
  {
    Icon: TbSos,
    color: RED, bg: LIGHT_RED, border: BORDER_RED,
    title: 'SOS Emergency Button',
    desc: 'One tap sends your location and trip details to our 24/7 safety team and your emergency contacts instantly.',
    points: ['One-tap SOS alert', 'Auto-shares location', 'Direct police connect', '24/7 safety team'],
  },
  {
    Icon: MdOutlineSupportAgent,
    color: '#7c3aed', bg: '#fdf4ff', border: '#e9b8ff',
    title: '24/7 Support',
    desc: 'Our dedicated safety team is available round the clock to assist you with any concern during or after your trip.',
    points: ['Round the clock support', 'In-app chat & call', 'Rapid response team', 'Post-trip follow-up'],
  },
  {
    Icon: BsCameraVideo,
    color: '#d97706', bg: '#fffbf0', border: '#fde68a',
    title: 'Cabin Monitoring',
    desc: 'Select vehicles are equipped with in-cabin cameras to ensure safety and deter any misconduct during rides.',
    points: ['In-cabin dashcam', 'Tamper alerts', 'Recording stored 72hrs', 'Accessible on request'],
  },
  {
    Icon: MdOutlineHealthAndSafety,
    color: '#0891b2', bg: '#f0f9ff', border: '#bae6fd',
    title: 'Hygiene Standards',
    desc: 'Our vehicles undergo regular sanitisation and our drivers follow strict hygiene protocols for every journey.',
    points: ['Regular sanitisation', 'Hygiene kit on board', 'Driver health checks', 'Clean vehicle guarantee'],
  },
];

const stats = [
  { value: '1M+', label: 'Safe Rides', Icon: BsShieldCheck },
  { value: '100%', label: 'Drivers Verified', Icon: HiOutlineBadgeCheck },
  { value: '24/7', label: 'Safety Support', Icon: MdOutlineSupportAgent },
  { value: '< 60s', label: 'SOS Response', Icon: TbSos },
];

const steps = [
  { step: '01', Icon: FiUser, title: 'Book Your Ride', desc: 'Select your route, vehicle, and travel date through the app or website.' },
  { step: '02', Icon: BsPersonCheck, title: 'Get Driver Details', desc: 'Receive verified driver photo, name, rating, and vehicle details before pickup.' },
  { step: '03', Icon: MdOutlineShareLocation, title: 'Share Trip Live', desc: 'Share your live trip with trusted contacts with a single tap before departure.' },
  { step: '04', Icon: TbSos, title: 'SOS If Needed', desc: 'In any emergency, press the SOS button — help is dispatched within 60 seconds.' },
];

const tips = [
  { Icon: FiCheckCircle, text: 'Always verify the driver name, photo, and vehicle number before boarding.' },
  { Icon: FiCheckCircle, text: 'Share your live trip link with at least one trusted contact.' },
  { Icon: FiCheckCircle, text: 'Sit in the back seat for added personal space and safety.' },
  { Icon: FiCheckCircle, text: 'Keep your phone charged. Use the in-app SOS if you feel unsafe.' },
  { Icon: FiCheckCircle, text: 'Do not share personal financial information with your driver.' },
  { Icon: FiCheckCircle, text: 'Rate your ride after the trip — your feedback protects future riders.' },
];

/* ─── PILLAR CARD — DESKTOP ─────────────────────────────────────────────────── */
const PillarCard = ({ pillar, index }) => {
  const { Icon } = pillar;
  return (
    <div style={{
      background: '#fff',
      border: `1.5px solid #e5e7eb`,
      borderRadius: '20px',
      padding: '28px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
      transition: 'transform 0.28s cubic-bezier(.22,.68,0,1.2), box-shadow 0.28s ease, border-color 0.2s',
      animation: `cardIn 0.45s ease both`,
      animationDelay: `${index * 0.07}s`,
      position: 'relative',
      overflow: 'hidden',
    }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.1)';
        e.currentTarget.style.borderColor = pillar.color;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.05)';
        e.currentTarget.style.borderColor = '#e5e7eb';
      }}
    >
      {/* Top accent line */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: '4px', background: pillar.color,
        borderRadius: '20px 20px 0 0',
      }} />

      {/* Icon */}
      <div style={{
        width: '52px', height: '52px', borderRadius: '16px',
        background: pillar.bg, border: `1.5px solid ${pillar.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon style={{ color: pillar.color, fontSize: '24px' }} />
      </div>

      <div>
        <h3 style={{
          fontSize: '17px', fontWeight: 800,
          color: '#111827', margin: '0 0 8px',
        }}>
          {pillar.title}
        </h3>
        <p style={{
          fontSize: '13.5px', color: '#6b7280',
          lineHeight: 1.65, margin: 0,
        }}>
          {pillar.desc}
        </p>
      </div>

      {/* Points */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {pillar.points.map(pt => (
          <div key={pt} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '18px', height: '18px', borderRadius: '50%',
              background: pillar.bg, border: `1px solid ${pillar.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <FiCheck style={{ color: pillar.color, fontSize: '10px', strokeWidth: 3 }} />
            </div>
            <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>{pt}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── PILLAR CARD — MOBILE ──────────────────────────────────────────────────── */
const MobilePillarCard = ({ pillar, index }) => {
  const [open, setOpen] = useState(false);
  const { Icon } = pillar;
  return (
    <div style={{
      background: '#fff',
      borderRadius: '18px',
      overflow: 'hidden',
      border: `1px solid ${open ? pillar.color : '#e5e7eb'}`,
      boxShadow: open ? `0 8px 24px ${pillar.color}22` : '0 2px 8px rgba(0,0,0,0.05)',
      transition: 'all 0.25s ease',
      animation: `cardIn 0.4s ease both`,
      animationDelay: `${index * 0.06}s`,
    }}>
      {/* Header row — tappable */}
      <button onClick={() => setOpen(p => !p)} style={{
        width: '100%', display: 'flex',
        alignItems: 'center', gap: '14px',
        padding: '16px 18px',
        background: 'none', border: 'none', cursor: 'pointer',
        textAlign: 'left',
      }}>
        {/* Left accent */}
        <div style={{
          width: '4px', height: '42px', borderRadius: '4px',
          background: pillar.color, flexShrink: 0,
        }} />

        <div style={{
          width: '42px', height: '42px', borderRadius: '13px',
          background: pillar.bg, border: `1px solid ${pillar.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon style={{ color: pillar.color, fontSize: '20px' }} />
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: 800, color: '#111827' }}>{pillar.title}</div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{pillar.desc.slice(0, 52)}…</div>
        </div>

        <div style={{
          width: '28px', height: '28px', borderRadius: '50%',
          background: open ? pillar.bg : '#f4f5f9',
          border: `1px solid ${open ? pillar.border : '#e5e7eb'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'all 0.2s',
        }}>
          <FiChevronRight style={{
            color: open ? pillar.color : '#9ca3af',
            fontSize: '14px',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.25s',
          }} />
        </div>
      </button>

      {/* Expanded content */}
      {open && (
        <div style={{
          padding: '0 18px 18px',
          animation: 'expandIn 0.2s ease',
        }}>
          <div style={{
            height: '1px', background: '#f0f0f0', marginBottom: '14px',
          }} />
          <p style={{
            fontSize: '13px', color: '#4b5563',
            lineHeight: 1.6, margin: '0 0 14px',
          }}>
            {pillar.desc}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pillar.points.map(pt => (
              <div key={pt} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%',
                  background: pillar.bg, border: `1px solid ${pillar.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <FiCheck style={{ color: pillar.color, fontSize: '10px', strokeWidth: 3 }} />
                </div>
                <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>{pt}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── MAIN PAGE ──────────────────────────────────────────────────────────────── */
const SafetyPage = () => {
  return (
    <div style={{ fontFamily: "'Segoe UI', 'SF Pro Display', -apple-system, sans-serif" }}>

      {/* ════════════════════════════ DESKTOP ════════════════════════════ */}
      <div className="safety-desktop">
        <div style={{ background: '#f4f5f9', minHeight: '100vh' }}>

          {/* Hero */}
          <div style={{
            background: '#fff',
            borderBottom: '1px solid #e5e7eb',
            padding: '64px 24px 56px',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Subtle dot grid */}
            {/* <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: 'radial-gradient(rgba(231,18,4,0.06) 1.5px, transparent 1.5px)',
              backgroundSize: '28px 28px',
              pointerEvents: 'none',
            }} /> */}
            {/* Right glow */}
            {/* <div style={{
              position: 'absolute', top: '-60px', right: '8%',
              width: '340px', height: '340px', borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(231,18,4,0.08) 0%, transparent 70%)',
              pointerEvents: 'none',
            }} /> */}

            <div style={{ maxWidth: '1060px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '48px' }}>

                {/* Left text */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '7px',
                    background: LIGHT_RED, border: `1px solid ${BORDER_RED}`,
                    borderRadius: '30px', padding: '7px 18px',
                    fontSize: '12px', fontWeight: 800, color: RED,
                    letterSpacing: '1.5px', marginBottom: '22px',
                  }}>
                    <FiShield size={14} /> YOUR SAFETY, OUR PRIORITY
                  </div>

                  <h1 style={{
                    fontSize: '56px', fontWeight: 900,
                    color: '#111827', lineHeight: 1.05,
                    letterSpacing: '-2px', margin: '0 0 18px',
                  }}>
                    Ride with<br />
                    <span style={{ color: RED }}>Complete</span> Confidence
                  </h1>

                  <p style={{
                    fontSize: '17px', color: '#6b7280',
                    maxWidth: '480px', lineHeight: 1.7,
                    margin: '0 0 32px', fontWeight: 400,
                  }}>
                    At BookinIndia, safety isn't a feature — it's the foundation. Every ride is backed by verified drivers, live tracking, and 24/7 emergency support.
                  </p>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button style={{
                      background: `linear-gradient(135deg, ${RED}, ${RED2})`,
                      color: '#fff', border: 'none',
                      borderRadius: '12px', padding: '14px 28px',
                      fontSize: '14px', fontWeight: 800, cursor: 'pointer',
                      boxShadow: `0 6px 20px rgba(231,18,4,0.35)`,
                      display: 'flex', alignItems: 'center', gap: '7px',
                      letterSpacing: '0.3px',
                    }}>
                      Book a Safe Ride <FiArrowRight />
                    </button>
                    <button style={{
                      background: '#fff',
                      color: '#111827',
                      border: '1.5px solid #e5e7eb',
                      borderRadius: '12px', padding: '14px 24px',
                      fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '7px',
                    }}>
                      <FiPhone style={{ color: RED }} /> Emergency: +91-63093 76010
                    </button>
                  </div>
                </div>

                {/* Right stats grid */}

<div style={{
//   height: '200px',
//   width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
//   marginRight:'-220px',
}}>
  <motion.img
    src={image}
    alt='logo'
    className='w-auto h-96'
    initial={{ scale: 1 }}
    animate={{ scale: 1.8 }}  // Grows to 80% larger
    transition={{ duration: 0.5 }}
    style={{
      transformOrigin: 'center center',
    }}
  />
</div>
              </div>
            </div>
          </div>

          {/* Safety Pillars */}
          <div style={{ maxWidth: '1060px', margin: '0 auto', padding: '56px 24px 0' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: LIGHT_RED, border: `1px solid ${BORDER_RED}`,
                borderRadius: '20px', padding: '5px 16px',
                fontSize: '11px', fontWeight: 800, color: RED,
                letterSpacing: '1.5px', marginBottom: '14px',
              }}>
                <BsShieldCheck size={13} /> SAFETY PILLARS
              </div>
              <h2 style={{ fontSize: '36px', fontWeight: 900, color: '#111827', letterSpacing: '-1px', margin: '0 0 10px' }}>
                6 Layers of Protection
              </h2>
              <p style={{ fontSize: '15px', color: '#9ca3af', margin: 0, maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' }}>
                Every BookinIndia ride is protected by multiple safety systems working together.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '20px',
            }}>
              {pillars.map((p, i) => <PillarCard key={p.title} pillar={p} index={i} />)}
            </div>
          </div>

          {/* How it works — horizontal stepper */}
          <div style={{ maxWidth: '1060px', margin: '0 auto', padding: '64px 24px 0' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: LIGHT_RED, border: `1px solid ${BORDER_RED}`,
                borderRadius: '20px', padding: '5px 16px',
                fontSize: '11px', fontWeight: 800, color: RED,
                letterSpacing: '1.5px', marginBottom: '14px',
              }}>
                <FiMapPin size={12} /> HOW SAFETY WORKS
              </div>
              <h2 style={{ fontSize: '36px', fontWeight: 900, color: '#111827', letterSpacing: '-1px', margin: 0 }}>
                Protected at Every Step
              </h2>
            </div>

            <div style={{ display: 'flex', gap: '0', position: 'relative' }}>
              {/* Connector line */}
              <div style={{
                position: 'absolute',
                top: '36px', left: '60px', right: '60px',
                height: '2px',
                background: `linear-gradient(90deg, ${RED}, ${RED2})`,
                opacity: 0.2,
                zIndex: 0,
              }} />

              {steps.map(({ step, Icon, title, desc }, i) => (
                <div key={step} style={{
                  flex: 1, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', textAlign: 'center',
                  padding: '0 16px', position: 'relative', zIndex: 1,
                  animation: `cardIn 0.45s ease both`,
                  animationDelay: `${i * 0.1}s`,
                }}>
                  <div style={{
                    width: '72px', height: '72px', borderRadius: '50%',
                    background: i === 0 ? `linear-gradient(135deg, ${RED}, ${RED2})` : '#fff',
                    border: `2.5px solid ${i === 0 ? RED : '#e5e7eb'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: i === 0 ? `0 8px 24px rgba(231,18,4,0.3)` : '0 4px 12px rgba(0,0,0,0.07)',
                    marginBottom: '18px',
                    transition: 'all 0.2s',
                  }}>
                    <Icon style={{ color: i === 0 ? '#fff' : RED, fontSize: '26px' }} />
                  </div>
                  <div style={{
                    fontSize: '11px', fontWeight: 800, color: RED,
                    letterSpacing: '1.5px', marginBottom: '6px',
                  }}>
                    STEP {step}
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#111827', marginBottom: '8px' }}>
                    {title}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6 }}>
                    {desc}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Safety Tips + SOS CTA */}
          <div style={{ maxWidth: '1060px', margin: '0 auto', padding: '64px 24px 80px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>

              {/* Tips card */}
              <div style={{
                background: '#fff',
                border: '1.5px solid #e5e7eb',
                borderRadius: '24px',
                padding: '32px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
              }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: LIGHT_RED, border: `1px solid ${BORDER_RED}`,
                  borderRadius: '20px', padding: '5px 14px',
                  fontSize: '11px', fontWeight: 800, color: RED,
                  letterSpacing: '1.5px', marginBottom: '20px',
                }}>
                  <FiStar size={12} /> RIDER SAFETY TIPS
                </div>
                <h3 style={{ fontSize: '22px', fontWeight: 900, color: '#111827', margin: '0 0 20px', letterSpacing: '-0.5px' }}>
                  Ride Smart. Stay Safe.
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {tips.map(({ Icon, text }) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <FiCheckCircle style={{ color: RED, fontSize: '18px', marginTop: '1px', flexShrink: 0 }} />
                      <span style={{ fontSize: '13.5px', color: '#374151', lineHeight: 1.55, fontWeight: 500 }}>{text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* SOS CTA */}
              <div style={{
                background: `linear-gradient(135deg, ${RED} 0%, ${RED2} 100%)`,
                borderRadius: '24px',
                padding: '32px',
                display: 'flex', flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: `0 12px 40px rgba(231,18,4,0.3)`,
                position: 'relative', overflow: 'hidden',
              }}>
                {/* Deco circles */}
                <div style={{
                  position: 'absolute', top: '-40px', right: '-40px',
                  width: '160px', height: '160px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                }} />
                <div style={{
                  position: 'absolute', bottom: '-30px', left: '20%',
                  width: '100px', height: '100px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.06)',
                }} />

                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{
                    width: '60px', height: '60px', borderRadius: '18px',
                    background: 'rgba(255,255,255,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '20px',
                    border: '1.5px solid rgba(255,255,255,0.3)',
                  }}>
                    <TbSos style={{ color: '#fff', fontSize: '30px' }} />
                  </div>

                  <h3 style={{ fontSize: '28px', fontWeight: 900, color: '#fff', margin: '0 0 12px', letterSpacing: '-0.5px' }}>
                    Emergency SOS
                  </h3>
                  <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.65, margin: '0 0 24px' }}>
                    Feeling unsafe? One tap on the SOS button shares your live location with our 24/7 safety team and your emergency contacts — response in under 60 seconds.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
                    {['Auto-shares your GPS location', 'Contacts our safety team instantly', 'Notifies your emergency contacts', 'Connects to local police if needed'].map(t => (
                      <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FiCheck style={{ color: '#fff', fontSize: '14px', strokeWidth: 3 }} />
                        <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ position: 'relative', zIndex: 1 }}>
                  <button style={{
                    width: '100%',
                    background: '#fff', color: RED,
                    border: 'none', borderRadius: '14px',
                    padding: '15px', fontSize: '15px',
                    fontWeight: 900, cursor: 'pointer',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '8px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    letterSpacing: '0.3px',
                  }}>
                    <FiPhone /> Call Helpline: +91-63093 76010
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════ MOBILE ════════════════════════════ */}
      <div className="safety-mobile">
        <div style={{
          background: '#f4f5f9',
          minHeight: '100vh',
          maxWidth: '430px',
          margin: '0 auto',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
          paddingBottom: '100px',
          marginTop:'-90px',
        }}>

          {/* App Header */}
          <div style={{
            background: '#fff',
            borderBottom: '1px solid #f0f0f0',
            padding: '52px 18px 24px',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: '-30px', right: '-30px',
              width: '130px', height: '130px', borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(231,18,4,0.07), transparent 70%)',
            }} />

            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: LIGHT_RED, border: `1px solid ${BORDER_RED}`,
              borderRadius: '20px', padding: '5px 14px',
              fontSize: '11px', fontWeight: 800, color: RED,
              letterSpacing: '1px', marginBottom: '12px',
            }}>
              <FiShield size={12} /> YOUR SAFETY, OUR PRIORITY
            </div>

            <h1 style={{
              fontSize: '28px', fontWeight: 900,
              color: '#111827', lineHeight: 1.15,
              letterSpacing: '-0.8px', margin: '0 0 10px',
            }}>
              Ride with<br />
              <span style={{ color: RED }}>Complete</span> Confidence
            </h1>
            <p style={{
              fontSize: '13px', color: '#9ca3af',
              margin: '0 0 20px', lineHeight: 1.55,
            }}>
              Every BookinIndia ride is backed by verified drivers, live tracking, and 24/7 emergency support.
            </p>

            {/* Stats 2×2 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {stats.map(({ value, label, Icon }) => (
                <div key={label} style={{
                  background: '#f4f5f9',
                  border: '1px solid #e5e7eb',
                  borderRadius: '14px', padding: '14px',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    background: LIGHT_RED,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon style={{ color: RED, fontSize: '17px' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: 900, color: '#111827', lineHeight: 1 }}>{value}</div>
                    <div style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 600, marginTop: '2px' }}>{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SOS Banner */}
          <div style={{ padding: '16px 18px 0' }}>
            <div style={{
              background: `linear-gradient(135deg, ${RED}, ${RED2})`,
              borderRadius: '20px',
              padding: '20px',
              display: 'flex', alignItems: 'center', gap: '16px',
              boxShadow: `0 8px 24px rgba(231,18,4,0.3)`,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', top: '-20px', right: '-20px',
                width: '90px', height: '90px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              }} />
              <div style={{
                width: '52px', height: '52px', borderRadius: '16px',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1.5px solid rgba(255,255,255,0.3)',
                flexShrink: 0,
              }}>
                <TbSos style={{ color: '#fff', fontSize: '26px' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: 900, color: '#fff', marginBottom: '3px' }}>Emergency SOS</div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>
                  One tap — location shared, help dispatched in &lt;60s
                </div>
              </div>
              <button style={{
                background: '#fff', color: RED,
                border: 'none', borderRadius: '12px',
                padding: '10px 16px', fontSize: '12px',
                fontWeight: 800, cursor: 'pointer',
                flexShrink: 0,
              }}>
                Call
              </button>
            </div>
          </div>

          {/* Safety Pillars accordion */}
          <div style={{ padding: '20px 18px 0' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '14px',
            }}>
              <span style={{
                fontSize: '17px', fontWeight: 800, color: '#111827',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                <BsShieldCheck style={{ color: RED }} /> Safety Pillars
              </span>
              <span style={{
                background: LIGHT_RED, color: RED,
                border: `1px solid ${BORDER_RED}`,
                borderRadius: '20px', padding: '3px 12px',
                fontSize: '11px', fontWeight: 700,
              }}>
                {pillars.length} features
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {pillars.map((p, i) => <MobilePillarCard key={p.title} pillar={p} index={i} />)}
            </div>
          </div>

          {/* How it works — vertical */}
          <div style={{ padding: '24px 18px 0' }}>
            <div style={{
              fontSize: '17px', fontWeight: 800, color: '#111827',
              display: 'flex', alignItems: 'center', gap: '6px',
              marginBottom: '14px',
            }}>
              <FiMapPin style={{ color: RED }} /> How Safety Works
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {steps.map(({ step, Icon, title, desc }, i) => (
                <div key={step} style={{ display: 'flex', gap: '14px', position: 'relative' }}>
                  {/* Vertical connector */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{
                      width: '44px', height: '44px', borderRadius: '50%',
                      background: i === 0 ? `linear-gradient(135deg, ${RED}, ${RED2})` : '#fff',
                      border: `2px solid ${i === 0 ? RED : '#e5e7eb'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: i === 0 ? `0 4px 14px rgba(231,18,4,0.3)` : '0 2px 8px rgba(0,0,0,0.07)',
                      zIndex: 1,
                    }}>
                      <Icon style={{ color: i === 0 ? '#fff' : RED, fontSize: '20px' }} />
                    </div>
                    {i < steps.length - 1 && (
                      <div style={{
                        width: '2px', height: '40px',
                        background: `linear-gradient(${RED}, #e5e7eb)`,
                        opacity: 0.3, marginTop: '2px',
                      }} />
                    )}
                  </div>

                  <div style={{ paddingBottom: i < steps.length - 1 ? '20px' : '0', paddingTop: '10px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 800, color: RED, letterSpacing: '1.5px', marginBottom: '3px' }}>
                      STEP {step}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: '#111827', marginBottom: '4px' }}>{title}</div>
                    <div style={{ fontSize: '12.5px', color: '#6b7280', lineHeight: 1.55 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Safety Tips */}
          <div style={{ padding: '24px 18px 0' }}>
            <div style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '20px',
              padding: '20px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                background: LIGHT_RED, border: `1px solid ${BORDER_RED}`,
                borderRadius: '20px', padding: '4px 12px',
                fontSize: '10px', fontWeight: 800, color: RED,
                letterSpacing: '1px', marginBottom: '14px',
              }}>
                <FiStar size={11} /> RIDER TIPS
              </div>
              <h3 style={{ fontSize: '16px', fontWeight: 800, color: '#111827', margin: '0 0 14px' }}>
                Ride Smart. Stay Safe.
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {tips.map(({ text }) => (
                  <div key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <FiCheckCircle style={{ color: RED, fontSize: '16px', marginTop: '1px', flexShrink: 0 }} />
                    <span style={{ fontSize: '13px', color: '#374151', lineHeight: 1.5, fontWeight: 500 }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Fixed bottom CTA */}
          <div style={{
            position: 'fixed', bottom: 0,
            left: '50%', transform: 'translateX(-50%)',
            width: '100%', maxWidth: '430px',
            padding: '12px 18px 28px',
            background: 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(12px)',
            borderTop: '1px solid #f0f0f0',
            zIndex: 200,
          }}>
            <button style={{
              width: '100%',
              background: `linear-gradient(135deg, ${RED} 0%, ${RED2} 100%)`,
              color: '#fff', border: 'none',
              borderRadius: '16px', padding: '16px',
              fontSize: '15px', fontWeight: 800,
              cursor: 'pointer',
              boxShadow: `0 6px 20px rgba(231,18,4,0.35)`,
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: '8px',
            }}>
              <FiShield style={{ fontSize: '18px' }} />
              Book a Safe Ride
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .safety-desktop { display: block; }
        .safety-mobile  { display: none;  }

        @media (max-width: 767px) {
          .safety-desktop { display: none !important;  }
          .safety-mobile  { display: block !important; }
        }

        @keyframes cardIn {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes expandIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0);    }
        }

        input::placeholder { color: #9ca3af; }
      `}</style>
    </div>
  );
};

export default SafetyPage;